import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import { LocationIcon, TargetIcon, CheckIcon, WarningIcon, XIcon } from './Icons';

// Leaflet icon fix for Vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const targetIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const userIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

interface MapProps {
    userLocation: { latitude: number; longitude: number };
    targetLocation: { latitude: number; longitude: number };
    address: string;
    isWithinRadius: boolean;
    className?: string;
}

/**
 * Component to adjust map view bounds
 */
function MapViewController({ userLocation, targetLocation }: { 
    userLocation: { latitude: number; longitude: number }, 
    targetLocation: { latitude: number; longitude: number } 
}) {
    const map = useMap();

    useEffect(() => {
        const bounds = L.latLngBounds([
            [userLocation.latitude, userLocation.longitude],
            [targetLocation.latitude, targetLocation.longitude]
        ]);
        
        map.fitBounds(bounds, { padding: [20, 20] });
    }, [map, userLocation, targetLocation]);

    return null;
}

export default function Map({ userLocation, targetLocation, address, isWithinRadius, className = '' }: MapProps) {
    const [mapError, setMapError] = useState<string | null>(null);

    // Calcula o centro do mapa (ponto médio entre as duas localizações)
    const center: [number, number] = [
        (userLocation.latitude + targetLocation.latitude) / 2,
        (userLocation.longitude + targetLocation.longitude) / 2
    ];

    if (mapError) {
        return (
            <div className={`bg-gray-100 border border-gray-300 rounded-lg p-4 text-center ${className}`}>
                <div className="flex items-center justify-center gap-2 text-gray-600 text-sm">
                    <XIcon size={16} className="text-red-500" />
                    <span>{mapError}</span>
                </div>
                <button 
                    onClick={() => setMapError(null)}
                    className="mt-2 text-blue-600 text-sm underline"
                >
                    Tentar novamente
                </button>
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            <div className="h-64 rounded-lg overflow-hidden shadow-md">
                <MapContainer
                    center={center}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                    touchZoom={true}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {/* Marcador da localização do usuário */}
                    <Marker 
                        position={[userLocation.latitude, userLocation.longitude]} 
                        icon={userIcon}
                    >
                        <Popup>
                            <div className="text-center">
                                <div className="flex items-center justify-center gap-2 mb-1">
                                    <LocationIcon size={16} className="text-blue-700" />
                                    <span className="font-medium text-blue-700">Sua Localização</span>
                                </div>
                                <p className="coordinates text-sm text-gray-600">
                                    {userLocation.latitude.toFixed(6)}, {userLocation.longitude.toFixed(6)}
                                </p>
                            </div>
                        </Popup>
                    </Marker>

                    {/* Marcador do endereço alvo */}
                    <Marker 
                        position={[targetLocation.latitude, targetLocation.longitude]} 
                        icon={targetIcon}
                    >
                        <Popup>
                            <div className="text-center">
                                <div className="flex items-center justify-center gap-2 mb-1">
                                    <TargetIcon size={16} className="text-red-700" />
                                    <span className="font-medium text-red-700">Destino</span>
                                </div>
                                <p className="text-sm text-gray-600 mb-1">{address}</p>
                                <p className="coordinates text-xs text-gray-500">
                                    {targetLocation.latitude.toFixed(6)}, {targetLocation.longitude.toFixed(6)}
                                </p>
                            </div>
                        </Popup>
                    </Marker>

                    {/* Círculo de 500m ao redor do destino */}
                    <Circle
                        center={[targetLocation.latitude, targetLocation.longitude]}
                        radius={500}
                        pathOptions={{
                            color: isWithinRadius ? '#10b981' : '#f59e0b',
                            fillColor: isWithinRadius ? '#10b981' : '#f59e0b',
                            fillOpacity: 0.1,
                            weight: 2,
                            dashArray: '5, 5'
                        }}
                    />

                    {/* Componente para ajustar a visualização */}
                    <MapViewController 
                        userLocation={userLocation} 
                        targetLocation={targetLocation} 
                    />
                </MapContainer>
            </div>

            {/* Legenda */}
            <div className="mt-2 flex justify-center gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span>Sua localização</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span>Destino</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className={`w-3 h-3 rounded-full border-2 ${isWithinRadius ? 'border-green-500' : 'border-yellow-500'}`}></div>
                    <span>Raio de 500m</span>
                </div>
            </div>

            {/* Status */}
            <div className="mt-2 text-center">
                <div className={`inline-flex items-center gap-2 text-sm font-medium px-2 py-2 rounded ${
                    isWithinRadius 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                }`}>
                    {isWithinRadius ? (
                        <CheckIcon size={16} className="text-green-600" />
                    ) : (
                        <WarningIcon size={16} className="text-yellow-600" />
                    )}
                    <span>{isWithinRadius ? 'Dentro do raio' : 'Fora do raio'}</span>
                </div>
            </div>
        </div>
    );
}
